// =============================================================================
// Admin Contacts Management Page (Phase II: Fundraising CRM)
// =============================================================================

import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  UserPlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EnvelopeIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  TagIcon,
  ArrowUpTrayIcon,
  XMarkIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import { Card, Button } from '../../../components/ui';
import { ContactFormModal } from './ContactFormModal';
import { fundraisingService } from '../../../services/fundraising.service';
import { getApiError, formatPhoneDisplay } from '../../../services/api';
import { usePermissions } from '../../../hooks/usePermissions';
import type {
  ContactWithSubscriptions,
  PipelineStage,
  LeadSource,
  ContactFilters,
} from '../../../types/fundraising';
import {
  PIPELINE_STAGE_LABELS,
  LEAD_SOURCE_LABELS,
} from '../../../types/fundraising';

// =============================================================================
// Pipeline Stage Badge Colors
// =============================================================================

const STAGE_COLORS: Record<PipelineStage, string> = {
  LEAD: 'bg-gray-100 text-gray-700',
  QUALIFIED: 'bg-blue-100 text-blue-700',
  DATA_ROOM: 'bg-indigo-100 text-indigo-700',
  INTERESTED: 'bg-purple-100 text-purple-700',
  SOFT_COMMIT: 'bg-yellow-100 text-yellow-800',
  HARD_COMMIT: 'bg-orange-100 text-orange-700',
  DOCS_SENT: 'bg-cyan-100 text-cyan-700',
  DOCS_SIGNED: 'bg-teal-100 text-teal-700',
  FUNDED: 'bg-green-100 text-green-700',
  CONVERTED: 'bg-emerald-100 text-emerald-800',
  PASSED: 'bg-red-100 text-red-700',
  INACTIVE: 'bg-gray-200 text-gray-500',
};

// =============================================================================
// Component
// =============================================================================

export function ContactsPage() {
  const { hasPermission, isLoaded: permsLoaded } = usePermissions();
  const canCreate = hasPermission('fundraising', 'create');
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [contacts, setContacts] = useState<ContactWithSubscriptions[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [stageFilter, setStageFilter] = useState<PipelineStage | ''>('');
  const [sourceFilter, setSourceFilter] = useState<LeadSource | ''>('');
  const [showFilters, setShowFilters] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  });

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    pipeline: 0,
    qualified: 0,
    funded: 0,
  });

  const loadContacts = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const filters: ContactFilters = {};
      if (searchQuery) filters.search = searchQuery;
      if (stageFilter) filters.pipelineStage = stageFilter;
      if (sourceFilter) filters.leadSource = sourceFilter;

      const result = await fundraisingService.listContacts(
        filters,
        pagination.page,
        pagination.limit
      );

      setContacts(result.contacts);
      setPagination(result.pagination);

      // Calculate stats from current page (in production you'd have a separate endpoint)
      const totalCommitment = result.contacts.reduce((sum, c) => {
        return sum + c.subscriptions.reduce((s, sub) => s + Number(sub.commitmentAmount), 0);
      }, 0);

      setStats({
        total: result.pagination.total,
        pipeline: totalCommitment,
        qualified: result.contacts.filter(c =>
          ['QUALIFIED', 'DATA_ROOM', 'INTERESTED'].includes(c.pipelineStage)
        ).length,
        funded: result.contacts.filter(c => c.pipelineStage === 'FUNDED').length,
      });
    } catch (err) {
      console.error('Failed to load contacts:', err);
      setError(getApiError(err).message);
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, stageFilter, sourceFilter, pagination.page, pagination.limit]);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination(p => ({ ...p, page: 1 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatTimeAgo = (dateString: string) => {
    const seconds = Math.floor((Date.now() - new Date(dateString).getTime()) / 1000);
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(dateString).toLocaleDateString();
  };

  const getTotalCommitment = (contact: ContactWithSubscriptions) => {
    return contact.subscriptions.reduce((sum, sub) => sum + Number(sub.commitmentAmount), 0);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStageFilter('');
    setSourceFilter('');
  };

  const hasFilters = searchQuery || stageFilter || sourceFilter;

  // Permission guard - early return after all hooks
  if (permsLoaded && !hasPermission('fundraising', 'view')) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <ShieldExclamationIcon className="h-12 w-12 text-gray-400 mb-3" />
        <h3 className="text-lg font-medium text-gray-900">Access Denied</h3>
        <p className="mt-1 text-sm text-gray-500">You do not have permission to view this page.</p>
      </div>
    );
  }

  if (isLoading && contacts.length === 0) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded" />
        <div className="h-12 bg-gray-200 rounded-lg" />
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-20 bg-gray-200 rounded-xl" />
          ))}
        </div>
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-24 bg-gray-200 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Contacts</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage prospective investors and track pipeline
          </p>
        </div>
        {canCreate && (
          <div className="flex items-center gap-3">
            <Button
              variant="secondary"
              className="flex items-center gap-2"
            >
              <ArrowUpTrayIcon className="h-4 w-4" />
              Import
            </Button>
            <Button className="flex items-center gap-2" onClick={() => setShowAddModal(true)}>
              <UserPlusIcon className="h-4 w-4" />
              Add Contact
            </Button>
          </div>
        )}
      </div>

      {/* Error Alert */}
      {error && (
        <Card padding="md">
          <div className="flex items-center gap-3 text-red-700">
            <span className="text-sm">Error: {error}</span>
            <button
              onClick={loadContacts}
              className="text-sm font-medium underline hover:no-underline"
            >
              Retry
            </button>
          </div>
        </Card>
      )}

      {/* Search & Filters */}
      <Card padding="md">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, or company..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-3 py-2 border rounded-lg transition-colors ${
                showFilters || hasFilters
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <FunnelIcon className="h-4 w-4" />
              Filters
              {hasFilters && (
                <span className="ml-1 px-1.5 py-0.5 text-xs bg-primary-500 text-white rounded-full">
                  {[searchQuery, stageFilter, sourceFilter].filter(Boolean).length}
                </span>
              )}
            </button>
            {hasFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
              >
                <XMarkIcon className="h-4 w-4" />
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Filter Dropdowns */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pipeline Stage
              </label>
              <select
                value={stageFilter}
                onChange={(e) => setStageFilter(e.target.value as PipelineStage | '')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">All Stages</option>
                {Object.entries(PIPELINE_STAGE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Lead Source
              </label>
              <select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value as LeadSource | '')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">All Sources</option>
                {Object.entries(LEAD_SOURCE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card padding="sm">
          <p className="text-sm text-gray-500">Total Contacts</p>
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
        </Card>
        <Card padding="sm">
          <p className="text-sm text-gray-500">Pipeline Value</p>
          <p className="text-2xl font-bold text-gray-900">
            {formatCurrency(stats.pipeline)}
          </p>
        </Card>
        <Card padding="sm">
          <p className="text-sm text-gray-500">Qualified Leads</p>
          <p className="text-2xl font-bold text-gray-900">{stats.qualified}</p>
        </Card>
        <Card padding="sm">
          <p className="text-sm text-gray-500">Funded</p>
          <p className="text-2xl font-bold text-green-600">{stats.funded}</p>
        </Card>
      </div>

      {/* Contacts List */}
      <div className="space-y-4">
        {contacts.map((contact) => (
          <Card key={contact.id} padding="md" hover>
            <Link to={`/admin/fundraising/contacts/${contact.id}`}>
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                {/* Contact Info */}
                <div className="flex items-start gap-4">
                  <div className="hidden sm:flex h-12 w-12 rounded-full bg-primary-100 items-center justify-center">
                    <span className="text-lg font-semibold text-primary-700">
                      {contact.firstName.charAt(0)}{contact.lastName.charAt(0)}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-semibold text-gray-900">
                        {contact.firstName} {contact.lastName}
                      </h3>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${STAGE_COLORS[contact.pipelineStage]}`}>
                        {PIPELINE_STAGE_LABELS[contact.pipelineStage]}
                      </span>
                    </div>
                    {contact.company && (
                      <div className="flex items-center gap-2 mt-1 text-sm text-gray-600">
                        <BuildingOfficeIcon className="h-4 w-4" />
                        {contact.company}
                        {contact.title && <span className="text-gray-400">• {contact.title}</span>}
                      </div>
                    )}
                    <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <EnvelopeIcon className="h-4 w-4" />
                        <span className="truncate max-w-[200px]">{contact.email}</span>
                      </div>
                      {contact.phone && (
                        <div className="flex items-center gap-1">
                          <PhoneIcon className="h-4 w-4" />
                          {formatPhoneDisplay(contact.phone)}
                        </div>
                      )}
                    </div>
                    {/* Tags */}
                    {contact.tags.length > 0 && (
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <TagIcon className="h-3.5 w-3.5 text-gray-400" />
                        {contact.tags.slice(0, 3).map((tag) => (
                          <span
                            key={tag}
                            className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded"
                          >
                            {tag}
                          </span>
                        ))}
                        {contact.tags.length > 3 && (
                          <span className="text-xs text-gray-400">
                            +{contact.tags.length - 3} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Stats */}
                <div className="flex items-center gap-6 text-right">
                  <div>
                    <p className="text-xs text-gray-500">Source</p>
                    <p className="text-sm font-medium text-gray-700">
                      {LEAD_SOURCE_LABELS[contact.leadSource]}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Commitment</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {contact.subscriptions.length > 0
                        ? formatCurrency(getTotalCommitment(contact))
                        : '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Added</p>
                    <p className="text-sm font-medium text-gray-700">
                      {formatTimeAgo(contact.createdAt)}
                    </p>
                  </div>
                </div>
              </div>
            </Link>
          </Card>
        ))}
      </div>

      {/* Empty State */}
      {contacts.length === 0 && !error && !isLoading && (
        <Card padding="lg">
          <div className="text-center py-12">
            <UserPlusIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-sm font-semibold text-gray-900">No contacts found</h3>
            <p className="mt-2 text-sm text-gray-500">
              {hasFilters
                ? 'Try adjusting your filters or search query.'
                : 'Get started by adding your first contact.'}
            </p>
            {!hasFilters && canCreate && (
              <div className="mt-6">
                <Button className="flex items-center gap-2 mx-auto" onClick={() => setShowAddModal(true)}>
                  <UserPlusIcon className="h-4 w-4" />
                  Add Contact
                </Button>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
            {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
            {pagination.total} contacts
          </p>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              disabled={pagination.page === 1}
              onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              disabled={pagination.page === pagination.totalPages}
              onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
            >
              Next
            </Button>
          </div>
        </div>
      )}
      {/* Add Contact Modal */}
      <ContactFormModal
        isOpen={showAddModal}
        contact={null}
        onClose={() => setShowAddModal(false)}
        onSaved={async () => {
          setShowAddModal(false);
          await loadContacts();
        }}
      />
    </div>
  );
}

export default ContactsPage;
